import {
	EmailCard,
	type EmailEntry,
	ThreadCard,
	groupByThread,
} from "@/components/email-thread";
import { api } from "@/lib/api";
import { cn, decodeHtmlEntities, relativeTime } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import {
	ArrowDownLeft,
	ArrowUpRight,
	ChevronDown,
	ChevronRight,
	Filter,
	Layers,
	List,
	Loader2,
	Mail,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

// ============================================================
// Emails Page — Global Email Inbox/Outbox Overview
// ============================================================

interface EmailListItem extends EmailEntry {
	bookingId: string | null;
	bookingKunde: string | null;
}

type DirectionFilter = "" | "inbox" | "sent";
type ViewMode = "flat" | "threads";

export function EmailsPage() {
	const [search, setSearch] = useState("");
	const [direction, setDirection] = useState<DirectionFilter>("");
	const [viewMode, setViewMode] = useState<ViewMode>("threads");
	const [expandedId, setExpandedId] = useState<string | null>(null);

	const query = useQuery({
		queryKey: ["emails", search, direction],
		queryFn: () => {
			const params = new URLSearchParams();
			if (search) params.set("search", search);
			if (direction) params.set("direction", direction);
			params.set("limit", "100");
			return api.get<{ data: EmailListItem[]; total: number }>(
				`/emails?${params}`,
			);
		},
	});

	const statsQuery = useQuery({
		queryKey: ["email-stats"],
		queryFn: () =>
			api.get<{
				data: {
					total: number;
					last24h: number;
					byDirection: Record<string, number>;
					byClassification: Record<string, number>;
				};
			}>("/emails/stats"),
	});

	const emails = query.data?.data ?? [];
	const total = query.data?.total ?? 0;
	const stats = statsQuery.data?.data;
	const threads = groupByThread(emails);

	return (
		<div className="space-y-6 animate-fade-in">
			<div className="flex items-center justify-between">
				<h1 className="font-display text-2xl">E-Mails</h1>
				{stats && (
					<div className="flex gap-3 text-xs text-stone-500">
						<span>{stats.total} gesamt</span>
						<span>{stats.last24h} letzte 24h</span>
					</div>
				)}
			</div>

			{stats && <StatsBar stats={stats} />}

			{/* Filters + View Toggle */}
			<div className="flex gap-3 items-center flex-wrap">
				<div className="relative flex-1 max-w-sm">
					<Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
					<input
						type="text"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder="E-Mail suchen..."
						className="input pl-9"
					/>
				</div>
				<div className="flex items-center gap-1">
					<Filter size={14} className="text-stone-400" />
					{(["", "inbox", "sent"] as const).map((d) => (
						<button
							key={d}
							type="button"
							onClick={() => setDirection(d)}
							className={cn(
								"px-3 py-1.5 text-xs rounded-md transition-colors",
								direction === d
									? "bg-brand-50 text-brand-700 font-medium"
									: "text-stone-500 hover:bg-stone-50",
							)}
						>
							{d === "" ? "Alle" : d === "inbox" ? "Eingang" : "Gesendet"}
						</button>
					))}
				</div>
				<div className="flex items-center gap-1 ml-auto">
					<button
						type="button"
						onClick={() => setViewMode("flat")}
						className={cn(
							"p-1.5 rounded-md transition-colors",
							viewMode === "flat"
								? "bg-brand-50 text-brand-700"
								: "text-stone-400 hover:bg-stone-50",
						)}
						title="Einzelansicht"
					>
						<List size={16} />
					</button>
					<button
						type="button"
						onClick={() => setViewMode("threads")}
						className={cn(
							"p-1.5 rounded-md transition-colors",
							viewMode === "threads"
								? "bg-brand-50 text-brand-700"
								: "text-stone-400 hover:bg-stone-50",
						)}
						title="Thread-Ansicht"
					>
						<Layers size={16} />
					</button>
				</div>
			</div>

			{/* Email List */}
			{query.isLoading ? (
				<div className="flex justify-center py-8">
					<Loader2 className="h-6 w-6 animate-spin text-stone-400" />
				</div>
			) : emails.length === 0 ? (
				<div className="text-center py-12 text-stone-400">
					Keine E-Mails gefunden.
				</div>
			) : viewMode === "flat" ? (
				<FlatView
					emails={emails}
					total={total}
					expandedId={expandedId}
					onToggle={(id) => setExpandedId(expandedId === id ? null : id)}
				/>
			) : (
				<ThreadView threads={threads} emailCount={emails.length} />
			)}
		</div>
	);
}

// ============================================================
// Stats Bar
// ============================================================

function StatsBar({
	stats,
}: {
	stats: {
		total: number;
		last24h: number;
		byDirection: Record<string, number>;
		byClassification: Record<string, number>;
	};
}) {
	return (
		<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
			<MiniCard
				label="Eingang"
				value={stats.byDirection.inbox ?? 0}
				color="text-blue-600"
			/>
			<MiniCard
				label="Gesendet"
				value={stats.byDirection.sent ?? 0}
				color="text-green-600"
			/>
			<MiniCard
				label="Buchungen"
				value={stats.byClassification.booking ?? 0}
				color="text-brand-600"
			/>
			<MiniCard
				label="Letzte 24h"
				value={stats.last24h}
				color="text-stone-600"
			/>
		</div>
	);
}

function MiniCard({
	label,
	value,
	color,
}: { label: string; value: number; color: string }) {
	return (
		<div className="card !py-3 text-center">
			<div className={cn("text-xl font-display", color)}>{value}</div>
			<div className="text-xs text-stone-400">{label}</div>
		</div>
	);
}

// ============================================================
// Thread View — Grouped by gmailThreadId
// ============================================================

function ThreadView({
	threads,
	emailCount,
}: { threads: ReturnType<typeof groupByThread>; emailCount: number }) {
	return (
		<div className="space-y-1">
			<p className="text-xs text-stone-400 mb-2">
				{emailCount} E-Mail{emailCount !== 1 ? "s" : ""} in {threads.length}{" "}
				Thread{threads.length !== 1 ? "s" : ""}
			</p>
			{threads.map((thread) => (
				<ThreadWithBody key={thread.threadId} thread={thread} />
			))}
		</div>
	);
}

function ThreadWithBody({
	thread,
}: { thread: ReturnType<typeof groupByThread>[0] }) {
	const bodyQuery = useQuery({
		queryKey: ["email-thread", thread.threadId],
		queryFn: () =>
			api.get<{ data: EmailListItem[] }>(`/emails/thread/${thread.threadId}`),
		enabled: false,
	});

	function handleExpand() {
		if (!bodyQuery.data) {
			bodyQuery.refetch();
		}
	}

	const fullEmails = bodyQuery.data?.data ?? thread.emails;

	return (
		<ThreadCard thread={thread} onExpand={handleExpand}>
			{fullEmails.map((email, idx) => (
				<EmailCard
					key={email.id}
					email={email}
					isLast={idx === fullEmails.length - 1}
					extra={<BookingLink email={email as EmailListItem} />}
				/>
			))}
		</ThreadCard>
	);
}

// ============================================================
// Flat View — Individual emails
// ============================================================

function FlatView({
	emails,
	total,
	expandedId,
	onToggle,
}: {
	emails: EmailListItem[];
	total: number;
	expandedId: string | null;
	onToggle: (id: string) => void;
}) {
	return (
		<div className="space-y-1">
			<p className="text-xs text-stone-400 mb-2">
				{total} Ergebnis{total !== 1 ? "se" : ""}
			</p>
			{emails.map((email) => (
				<EmailRow
					key={email.id}
					email={email}
					expanded={expandedId === email.id}
					onToggle={() => onToggle(email.id)}
				/>
			))}
		</div>
	);
}

function EmailRow({
	email,
	expanded,
	onToggle,
}: {
	email: EmailListItem;
	expanded: boolean;
	onToggle: () => void;
}) {
	const isInbound = email.direction === "inbox";

	return (
		<div className="card !p-0 overflow-hidden">
			<button
				type="button"
				onClick={onToggle}
				className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-stone-50 transition-colors"
			>
				<div
					className={cn(
						"shrink-0 rounded-full p-1.5",
						isInbound
							? "bg-blue-50 text-blue-600"
							: "bg-green-50 text-green-600",
					)}
				>
					{isInbound ? <ArrowDownLeft size={12} /> : <ArrowUpRight size={12} />}
				</div>
				<div className="flex-1 min-w-0">
					<div className="flex items-center gap-2">
						<span className="text-sm font-medium text-stone-700 truncate">
							{email.subject ?? "(Kein Betreff)"}
						</span>
						{email.classified && (
							<span
								className={cn(
									"badge text-[10px] shrink-0",
									classificationColor(email.classified),
								)}
							>
								{email.classified}
							</span>
						)}
					</div>
					<div className="flex items-center gap-2 text-xs text-stone-500 mt-0.5">
						<span className="truncate">
							{isInbound ? email.fromEmail : email.toEmail}
						</span>
						<BookingLink email={email} />
					</div>
				</div>
				<div className="flex items-center gap-2 shrink-0">
					<span className="text-xs text-stone-400">
						{email.gmailDate
							? relativeTime(email.gmailDate)
							: email.processedAt
								? relativeTime(email.processedAt)
								: "—"}
					</span>
					{expanded ? (
						<ChevronDown size={14} className="text-stone-400" />
					) : (
						<ChevronRight size={14} className="text-stone-400" />
					)}
				</div>
			</button>

			{expanded && email.snippet && (
				<div className="px-4 py-3 border-t border-stone-100 bg-stone-50">
					<p className="text-xs text-stone-600 leading-relaxed">
						{decodeHtmlEntities(email.snippet)}
					</p>
					{email.bookingId && (
						<Link
							to={`/bookings/${email.bookingId}`}
							className="text-xs text-brand-600 hover:underline mt-2 inline-block"
						>
							Zur Buchung
						</Link>
					)}
				</div>
			)}
		</div>
	);
}

// ============================================================
// Helpers
// ============================================================

function BookingLink({ email }: { email: EmailListItem }) {
	if (!email.bookingKunde || !email.bookingId) return null;
	return (
		<Link
			to={`/bookings/${email.bookingId}`}
			className="text-xs text-brand-600 hover:underline shrink-0 mt-1"
			onClick={(e) => e.stopPropagation()}
		>
			{email.bookingKunde}
		</Link>
	);
}

function classificationColor(type: string): string {
	const colors: Record<string, string> = {
		booking: "bg-blue-50 text-blue-700",
		angebot: "bg-amber-50 text-amber-700",
		ab: "bg-emerald-50 text-emerald-700",
		rechnung: "bg-orange-50 text-orange-700",
		stornierung: "bg-red-50 text-red-700",
		"follow-up": "bg-purple-50 text-purple-700",
		irrelevant: "bg-stone-100 text-stone-500",
	};
	return colors[type] ?? "bg-stone-100 text-stone-500";
}
